import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles,
  Clapperboard,
  Download,
  Copy,
  Check,
  Calendar,
  Layers,
  CircleDollarSign,
  AlertCircle,
  FileText,
  Clock,
  Film,
  X,
  ExternalLink,
} from "lucide-react";
import { StatusPill, DemoBadge } from "@/components/common/States";
import { formatExactDateTime, formatBytes } from "@/lib/dateUtils";
import { formatResolutionLabel, isKlingPro } from "@/domain/resolutionLabels";
import { downloadAsset } from "@/components/common/ImageActionBar";
import { toast } from "sonner";
import { t } from "@/i18n";

export default function JobDetailModal({
  job,
  open,
  onOpenChange,
  assetById = {},
  projectById = {},
  sceneById = {},
  lang = "es",
}) {
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [extraMeta, setExtraMeta] = useState(null);
  const [naturalDimensions, setNaturalDimensions] = useState(null);

  // Localizar el asset resultante
  const resultId = job ? (job.resultAssetId || (job.resultAssetIds && job.resultAssetIds[0]) || null) : null;
  const asset = resultId ? assetById[resultId] : null;

  // Si el modal está abierto y el asset no tiene metadatos completos, consultar endpoint de upload
  useEffect(() => {
    setNaturalDimensions(null);
    if (!open || !resultId) {
      setExtraMeta(null);
      return;
    }
    let active = true;
    const token = localStorage.getItem("token") || "";
    fetch(`/api/uploads/${resultId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (active && data) setExtraMeta(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [open, resultId]);

  const effectiveAsset = useMemo(() => ({
    ...(asset || {}),
    ...(extraMeta || {}),
  }), [asset, extraMeta]);

  if (!job) return null;

  const assetUrl = effectiveAsset.url || asset?.url || (resultId ? `/api/uploads/${resultId}/file` : null);

  // Localizar referencias de imagen inicial y final
  const startAssetId = job.sourceAssetId || job.params?.source_asset_id || job.params?.startAssetId;
  const startAsset = startAssetId ? assetById[startAssetId] : null;

  const endAssetId = job.endAssetId || job.params?.end_asset_id || job.params?.endAssetId;
  const endAsset = endAssetId ? assetById[endAssetId] : null;

  const motionVideoId = job.motionVideoAssetId || job.params?.motion_video_asset_id;
  const motionVideoAsset = motionVideoId ? assetById[motionVideoId] : null;

  const handleCopyPrompt = () => {
    if (!job.prompt) return;
    navigator.clipboard.writeText(job.prompt);
    setCopiedPrompt(true);
    toast.success("Prompt copiado al portapapeles");
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  const handleCopyId = () => {
    const idToCopy = job.backendJobId || job.id;
    if (!idToCopy) return;
    navigator.clipboard.writeText(idToCopy);
    setCopiedId(true);
    toast.success("Identificador de tarea copiado");
    setTimeout(() => setCopiedId(false), 2000);
  };

  const isVideo = job.kind === "video";
  const params = job.params || {};

  // Formatear resolución con etiquetas claras
  const rawRes = params.resolution || params.mode || job.resolution;
  const resolutionDisplay = formatResolutionLabel(rawRes, job.model || job.modelId);
  const isPro = isKlingPro(rawRes, job.model || job.modelId);

  // Multishot
  const isMultishot = Boolean(params.multi_shot || params.multi_prompt || params.shots);
  const multiPromptShots = Array.isArray(params.multi_prompt)
    ? params.multi_prompt
    : Array.isArray(params.shots)
    ? params.shots
    : [];

  // Datos económicos
  const confirmedCost = job.costReal != null ? job.costReal : (job.confirmedCostUsd != null ? job.confirmedCostUsd : null);
  const estimatedCost = job.costEstimate != null ? job.costEstimate : (job.estimatedCostUsd != null ? job.estimatedCostUsd : null);
  const reserveUsd = job.reserveUsd != null ? job.reserveUsd : (params.reserve != null ? params.reserve : null);

  const hasConfirmedCost = confirmedCost != null && !isNaN(Number(confirmedCost));
  const fullModelName = job.model || job.modelId || "No disponible";

  // 1. Dimensiones verificadas del archivo resultante
  // Procedencia demostrable: metadatos de decodificación Pillow/ffprobe en backend o naturalWidth/naturalHeight en cliente.
  // NUNCA tomar params.width/height como si fueran el archivo real.
  const verifiedWidth = effectiveAsset.width || asset?.width || naturalDimensions?.width || null;
  const verifiedHeight = effectiveAsset.height || asset?.height || naturalDimensions?.height || null;
  const verifiedDimensionsDisplay = (verifiedWidth && verifiedHeight)
    ? `${verifiedWidth} × ${verifiedHeight} px`
    : "No disponible";

  // 2. Parámetros solicitados en el trabajo (solicitud enviada al modelo)
  const requestedWidth = params.width || (typeof params.resolution === "string" && params.resolution.includes("x") ? params.resolution.split("x")[0] : null);
  const requestedHeight = params.height || (typeof params.resolution === "string" && params.resolution.includes("x") ? params.resolution.split("x")[1] : null);
  const requestedSizeDisplay = (requestedWidth && requestedHeight)
    ? `${requestedWidth} × ${requestedHeight} px`
    : (params.resolution || null);

  const aspectRatioDisplay = params.aspect_ratio || params.format || effectiveAsset.aspect_ratio || (verifiedWidth && verifiedHeight ? `${verifiedWidth}:${verifiedHeight}` : null) || "No disponible";

  const fileFormat = effectiveAsset.mime
    ? effectiveAsset.mime.replace("video/", "").replace("image/", "").toUpperCase()
    : params.output_format
    ? params.output_format.toUpperCase()
    : isVideo
    ? "MP4"
    : "No disponible";

  const fileSize = effectiveAsset.size != null
    ? formatBytes(effectiveAsset.size)
    : effectiveAsset.sizeBytes != null
    ? formatBytes(effectiveAsset.sizeBytes)
    : "No disponible";

  const fpsDisplay = effectiveAsset.fps != null
    ? `${effectiveAsset.fps % 1 === 0 ? effectiveAsset.fps : Number(effectiveAsset.fps).toFixed(1)} fps`
    : params.fps
    ? `${params.fps} fps`
    : "No disponible";

  const videoDuration = effectiveAsset.duration != null
    ? `${Number(effectiveAsset.duration).toFixed(2)} s`
    : params.duration
    ? `${params.duration} segundos`
    : isVideo
    ? "No disponible"
    : "No aplica";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="job-detail-modal"
        className="max-w-3xl max-h-[90vh] overflow-y-auto border border-[#303030] bg-[#141414] p-6 text-[#ededed] shadow-2xl"
      >
        <DialogHeader className="border-b border-[#262626] pb-3 text-left">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              {isVideo ? (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 text-[#f5b544]">
                  <Clapperboard size={18} />
                </div>
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 text-[#f5b544]">
                  <Sparkles size={18} />
                </div>
              )}
              <div>
                <DialogTitle className="text-base font-semibold text-[#f4f1ec]">
                  Detalles de la generación
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Características técnicas, económicas y parámetros de la generación
                </DialogDescription>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-[#a3a3a3]">
                  <span className="font-mono text-[#8a8a8a]">{job.backendJobId || job.id}</span>
                  <span>·</span>
                  <span>{formatExactDateTime(job.createdAt, lang)}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={job.status} map={t.tasks.status} />
              {job.isDemo && <DemoBadge label={t.tasks.demoTag} />}
              {job.isSimulation && <DemoBadge label={t.tasks.simTag} />}
            </div>
          </div>
        </DialogHeader>

        <div className="mt-4 space-y-5">
          {/* ── 1. Vista previa del archivo ── */}
          <div className="rounded-xl border border-[#262626] bg-[#0d0d0d] p-3 overflow-hidden">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
                Vista previa del resultado
              </span>
              {assetUrl && (
                <button
                  type="button"
                  data-testid="job-detail-download-btn"
                  onClick={() => downloadAsset({
                    ...(asset || {}),
                    ...(effectiveAsset || {}),
                    id: resultId || asset?.id || effectiveAsset?.id,
                    url: assetUrl,
                    type: job.kind || (isVideo ? "video" : "image"),
                    prompt: job.prompt,
                  })}
                  className="v-btn-ghost v-btn-xs inline-flex items-center gap-1.5 text-xs text-[#f5b544] hover:text-[#ffd17d]"
                >
                  <Download size={13} />
                  <span>Descargar archivo original</span>
                </button>
              )}
            </div>

            {assetUrl ? (
              <div className="relative flex items-center justify-center bg-black rounded-lg overflow-hidden max-h-[380px]">
                {isVideo ? (
                  <video
                    data-testid="job-detail-video-player"
                    src={assetUrl}
                    controls
                    playsInline
                    className="w-full max-h-[360px] object-contain"
                    onLoadedMetadata={(e) => {
                      const vid = e.currentTarget;
                      if (vid.videoWidth && vid.videoHeight) {
                        setNaturalDimensions({ width: vid.videoWidth, height: vid.videoHeight });
                      }
                    }}
                  />
                ) : (
                  <img
                    data-testid="job-detail-image-preview"
                    src={assetUrl}
                    alt={job.prompt || "Resultado"}
                    className="w-full max-h-[360px] object-contain"
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (img.naturalWidth && img.naturalHeight) {
                        setNaturalDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                      }
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center text-xs text-[#737373] bg-[#141414] rounded-lg">
                <AlertCircle size={28} className="mb-2 text-[#525252]" />
                <p className="font-medium text-[#a3a3a3]">
                  {job.status === "failed"
                    ? "La generación no produjo archivo debido a un error."
                    : job.status === "blocked"
                    ? "Tarea bloqueada: no se envió al proveedor."
                    : job.status === "running"
                    ? "Generación en curso en el proveedor..."
                    : "Archivo original no disponible en almacenamiento local."}
                </p>
                {job.errorDetail && (
                  <p className="mt-1 max-w-md font-mono text-[11px] text-red-400/90 break-words">
                    {job.errorDetail}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ── 2. Información económica ── */}
          <div className="rounded-xl border border-[#262626] bg-[#171717] p-4">
            <div className="flex items-center gap-2 mb-3">
              <CircleDollarSign size={15} className="text-[#f5b544]" />
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#d8d4cd]">
                Información económica
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {/* Coste real liquidado */}
              <div className={`rounded-lg border p-3 ${
                hasConfirmedCost
                  ? "border-emerald-500/25 bg-emerald-500/5"
                  : "border-[#2a2a2a] bg-[#141414]"
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-mono text-[#8a8a8a]">
                    Coste real
                  </span>
                  <span
                    data-testid="job-cost-confirmed-badge"
                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                      hasConfirmedCost
                        ? "bg-emerald-500/10 text-emerald-400"
                        : "bg-[#262626] text-[#a3a3a3]"
                    }`}
                  >
                    {hasConfirmedCost
                      ? "Confirmado"
                      : (job.status === "running" ? "Pendiente" : "No reportado")}
                  </span>
                </div>
                <div className={`mt-1 text-lg font-mono font-bold ${hasConfirmedCost ? "text-emerald-400" : "text-[#d8d4cd]"}`}>
                  {hasConfirmedCost
                    ? `$${Number(confirmedCost).toFixed(4)} USD`
                    : <span className="text-xs font-normal text-[#8a8a8a]">{job.status === "running" ? "Pendiente de confirmación" : "No reportado"}</span>}
                </div>
                <p className="mt-1 text-[10px] text-[#8a8a8a] leading-tight">
                  {hasConfirmedCost
                    ? "Tarifa final liquidada y confirmada por el proveedor."
                    : (job.status === "running"
                        ? "Importe pendiente de confirmación por la respuesta del proveedor."
                        : "Importe no confirmado por la respuesta del proveedor: sin evidencia de facturación.")}
                </p>
              </div>

              {/* Coste estimado antes de generar */}
              <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-mono text-[#8a8a8a]">
                    Coste estimado
                  </span>
                  <span className="inline-flex items-center rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#f5b544]">
                    Estimado previo
                  </span>
                </div>
                <div className="mt-1 text-lg font-mono font-bold text-[#f5b544]">
                  {isPro && !hasConfirmedCost ? (
                    <span className="text-xs font-normal text-amber-300">Tarifa PRO no confirmada</span>
                  ) : estimatedCost != null ? (
                    `$${Number(estimatedCost).toFixed(4)} USD`
                  ) : (
                    <span className="text-xs font-normal text-[#8a8a8a]">No disponible</span>
                  )}
                </div>
                <p className="mt-1 text-[10px] text-[#8a8a8a] leading-tight">
                  {isPro
                    ? "Proveedor no publica tarifa fija para modalidad PRO."
                    : "Cálculo previo antes de iniciar la solicitud."}
                </p>
              </div>

              {/* Reserva preventiva */}
              <div className="rounded-lg border border-[#2a2a2a] bg-[#141414] p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-mono text-[#8a8a8a]">
                    Reserva preventiva
                  </span>
                  <span className="text-[9px] font-mono text-[#8a8a8a]">Reserva</span>
                </div>
                <div className="mt-1 text-lg font-mono font-medium text-[#d8d4cd]">
                  {reserveUsd != null
                    ? `$${Number(reserveUsd).toFixed(4)} USD`
                    : <span className="text-xs font-normal text-[#8a8a8a]">No disponible</span>}
                </div>
                <p className="mt-1 text-[10px] text-[#8a8a8a] leading-tight">
                  Saldo bloqueado cautelarmente durante la ejecución.
                </p>
              </div>
            </div>
          </div>

          {/* ── 3. Información técnica ── */}
          <div className="rounded-xl border border-[#262626] bg-[#171717] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Layers size={15} className="text-[#f5b544]" />
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#d8d4cd]">
                Información técnica
              </h4>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-4 text-xs">
              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]">{t.gallery?.type || "Tipo"}</dt>
                <dd className="mt-0.5 font-medium text-[#ededed]">
                  {isVideo ? (t.gallery?.filters?.video || "Vídeo") : (t.gallery?.filters?.image || "Imagen")}
                </dd>
              </div>

              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]">{t.tools?.model || "Modelo"}</dt>
                <dd
                  className="mt-0.5 font-medium text-[#ededed] break-all"
                  title={fullModelName}
                  data-testid="job-detail-model-name"
                >
                  <span className="line-clamp-2" title={fullModelName}>{fullModelName}</span>
                </dd>
              </div>

              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]">{t.storyboard?.originProvider || "Proveedor"}</dt>
                <dd className="mt-0.5 font-medium text-[#ededed]">
                  {job.providerId === "apimart" ? "APIMart" : (job.providerId === "fal" ? "fal.ai" : (job.providerId || "No disponible"))}
                </dd>
              </div>

              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]">Resolución / Modo</dt>
                <dd className="mt-0.5 font-medium text-[#ededed] truncate" title={resolutionDisplay}>
                  {resolutionDisplay}
                </dd>
              </div>

              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]" title="Dimensiones reales verificadas del archivo resultante">
                  {t.gallery?.realDimensions || "Dimensiones reales"}
                </dt>
                <dd className="mt-0.5 font-medium font-mono text-[#ededed] truncate" data-testid="job-detail-dimensions">
                  {verifiedDimensionsDisplay}
                </dd>
              </div>

              {requestedSizeDisplay && (
                <div className="rounded bg-[#121212] p-2.5 border border-[#242424]" data-testid="job-detail-requested-size-card">
                  <dt className="text-[10px] uppercase font-mono text-[#737373]" title="Tamaño solicitado en los parámetros del trabajo">
                    {t.tasks?.requestedSize || "Tamaño solicitado"}
                  </dt>
                  <dd className="mt-0.5 font-medium font-mono text-[#a3a3a3] truncate" data-testid="job-detail-requested-size">
                    {requestedSizeDisplay}
                  </dd>
                </div>
              )}

              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]">{t.gallery?.aspectRatio || "Relación aspecto"}</dt>
                <dd className="mt-0.5 font-medium text-[#ededed]" data-testid="job-detail-aspect-ratio">
                  {aspectRatioDisplay}
                </dd>
              </div>

              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]">Formato</dt>
                <dd className="mt-0.5 font-medium text-[#ededed]">
                  {fileFormat}
                </dd>
              </div>

              <div className="rounded bg-[#121212] p-2.5 border border-[#242424]">
                <dt className="text-[10px] uppercase font-mono text-[#737373]">Tamaño de archivo</dt>
                <dd className="mt-0.5 font-medium text-[#ededed]">
                  {fileSize}
                </dd>
              </div>

              {/* Solo para vídeos: Duración, FPS, Audio nativo, Modalidad de vídeo */}
              {isVideo && (
                <>
                  <div className="rounded bg-[#121212] p-2.5 border border-[#242424]" data-testid="job-detail-video-duration">
                    <dt className="text-[10px] uppercase font-mono text-[#737373]">Duración</dt>
                    <dd className="mt-0.5 font-medium text-[#ededed]">
                      {videoDuration}
                    </dd>
                  </div>

                  <div className="rounded bg-[#121212] p-2.5 border border-[#242424]" data-testid="job-detail-video-fps">
                    <dt className="text-[10px] uppercase font-mono text-[#737373]">FPS</dt>
                    <dd className="mt-0.5 font-medium text-[#ededed]">
                      {fpsDisplay}
                    </dd>
                  </div>

                  <div className="rounded bg-[#121212] p-2.5 border border-[#242424]" data-testid="job-detail-video-audio">
                    <dt className="text-[10px] uppercase font-mono text-[#737373]">Audio nativo</dt>
                    <dd className="mt-0.5 font-medium text-[#ededed]">
                      {params.audio ? "Habilitado" : "Desactivado"}
                    </dd>
                  </div>

                  <div className="rounded bg-[#121212] p-2.5 border border-[#242424]" data-testid="job-detail-video-mode">
                    <dt className="text-[10px] uppercase font-mono text-[#737373]">Modalidad vídeo</dt>
                    <dd className="mt-0.5 font-medium text-[#ededed]">
                      {params.video_mode || "No disponible"}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {/* ── 4. Información de generación y prompts ── */}
          <div className="rounded-xl border border-[#262626] bg-[#171717] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText size={15} className="text-[#f5b544]" />
                <h4 className="text-xs font-semibold uppercase tracking-wider text-[#d8d4cd]">
                  Prompt completo y contexto
                </h4>
              </div>
              <button
                type="button"
                data-testid="job-detail-copy-prompt"
                onClick={handleCopyPrompt}
                className="v-btn-ghost v-btn-xs inline-flex items-center gap-1 text-[11px] text-[#a3a3a3] hover:text-[#f4f1ec]"
              >
                {copiedPrompt ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                <span>{copiedPrompt ? "Copiado" : "Copiar prompt"}</span>
              </button>
            </div>

            <div className="rounded-lg bg-[#0d0d0d] p-3 border border-[#242424]">
              <p className="text-xs leading-relaxed text-[#ededed] whitespace-pre-wrap select-all font-sans">
                {job.prompt || "Sin prompt registrado."}
              </p>
            </div>

            {/* Referencias Start Frame / End Frame */}
            {(startAsset || endAsset || motionVideoAsset || startAssetId || endAssetId) && (
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 pt-2 border-t border-[#262626]">
                {(startAsset || startAssetId) && (
                  <div className="flex items-center gap-2.5 rounded bg-[#121212] p-2 border border-[#242424]">
                    {startAsset?.url ? (
                      <img src={startAsset.url} alt="Start Frame" className="h-10 w-10 rounded object-cover border border-[#333]" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded bg-[#202020] text-[10px] text-[#737373]">IMG</div>
                    )}
                    <div className="min-w-0 flex-1 text-[11px]">
                      <div className="font-semibold text-emerald-400">Start Frame (Fotograma Inicial)</div>
                      <div className="truncate font-mono text-[10px] text-[#8a8a8a]">{startAssetId}</div>
                    </div>
                  </div>
                )}

                {(endAsset || endAssetId) && (
                  <div className="flex items-center gap-2.5 rounded bg-[#121212] p-2 border border-[#242424]">
                    {endAsset?.url ? (
                      <img src={endAsset.url} alt="End Frame" className="h-10 w-10 rounded object-cover border border-[#333]" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded bg-[#202020] text-[10px] text-[#737373]">IMG</div>
                    )}
                    <div className="min-w-0 flex-1 text-[11px]">
                      <div className="font-semibold text-indigo-400">End Frame (Fotograma Final)</div>
                      <div className="truncate font-mono text-[10px] text-[#8a8a8a]">{endAssetId}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Configuración Multishot individual */}
            {isMultishot && multiPromptShots.length > 0 && (
              <div className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[#f5b544]">
                    <Film size={13} />
                    <span>Tomas Multishot registradas ({multiPromptShots.length} tomas)</span>
                  </div>
                  <span className="text-[10px] font-mono text-[#a3a3a3]">
                    Duración total: {params.duration || 5}s
                  </span>
                </div>

                <div className="space-y-2">
                  {multiPromptShots.map((shot, idx) => (
                    <div
                      key={idx}
                      className="rounded bg-[#141414] p-2.5 border border-[#2a2a2a] text-xs text-[#d8d4cd]"
                    >
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="font-mono font-bold text-[#f5b544]">
                          Toma {shot.index || idx + 1}
                        </span>
                        <span className="rounded bg-[#202020] px-1.5 py-0.5 font-mono text-[10px] text-[#a3a3a3]">
                          {shot.duration} segundos
                        </span>
                      </div>
                      <p className="italic text-[11px] text-[#ededed]">
                        "{shot.prompt}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadatos adicionales de contexto */}
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-[#8a8a8a] pt-2 border-t border-[#262626]">
              <div>
                <span className="text-[10px] uppercase font-mono text-[#666]">Proyecto asociado:</span>
                <div className="text-[#ededed] font-medium">
                  {projectById[job.projectId]?.name || "Sin proyecto asociado"}
                </div>
              </div>
              {sceneById[job.sceneId] && (
                <div>
                  <span className="text-[10px] uppercase font-mono text-[#666]">Escena del guion:</span>
                  <div className="text-[#ededed] font-medium">
                    {sceneById[job.sceneId].title}
                  </div>
                </div>
              )}
              <div>
                <span className="text-[10px] uppercase font-mono text-[#666]">ID de tarea:</span>
                <div className="flex items-center gap-1 font-mono text-[11px] text-[#d8d4cd]">
                  <span className="truncate">{job.backendJobId || job.id}</span>
                  <button
                    type="button"
                    onClick={handleCopyId}
                    title="Copiar ID"
                    className="text-[#666] hover:text-[#f5b544]"
                  >
                    {copiedId ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                  </button>
                </div>
              </div>
              {job.externalTaskId && (
                <div>
                  <span className="text-[10px] uppercase font-mono text-[#666]">ID externo del proveedor:</span>
                  <div className="font-mono text-[11px] text-[#d8d4cd] truncate" title={job.externalTaskId}>
                    {job.externalTaskId}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
